import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import moment from 'moment';
import DangerButton from './DangerButton';
import DateField from './DateField';
import { OrderEarlyEndRequest } from './types';
import {
  MAX_REASON_LENGTH,
  ORDER_END_REASONS,
  OrderEndReason,
  toOrderEndReason,
  validateOrderEndReason,
} from './utils';

interface ReasonState {
  reason: OrderEndReason | '';
  other: string;
}

type ReasonErrors = { reason?: string; other?: string };

const EMPTY_REASON: ReasonState = { reason: '', other: '' };

// The required reason of Cancel and End early: a select of common reasons
// and a required text for "Other" (owner, 24.10, D9). An error clears as
// soon as the manager changes its field (B3).
const ReasonFields = ({
  id,
  label,
  value,
  errors,
  autoFocus,
  onChange,
}: {
  id: string;
  label: string;
  value: ReasonState;
  errors: ReasonErrors;
  autoFocus?: boolean;
  onChange: (value: ReasonState, field: keyof ReasonErrors) => void;
}) => {
  const intl = useIntl();
  return (
    <>
      <TextField
        id={id}
        select
        autoFocus={autoFocus}
        fullWidth
        required
        variant="outlined"
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        value={value.reason}
        onChange={(event) =>
          onChange(
            { ...value, reason: event.target.value as OrderEndReason },
            'reason',
          )
        }
        label={label}
        error={Boolean(errors.reason)}
        helperText={intl.formatMessage({
          id: errors.reason || 'ORDERS.INTERNAL_REASON_HELP',
        })}
      >
        <option value="" disabled>
          {intl.formatMessage({ id: 'ORDERS.REASON.CHOOSE' })}
        </option>
        {ORDER_END_REASONS.map(({ id: reason }) => (
          <option key={reason} value={reason}>
            {intl.formatMessage({ id: `ORDERS.REASON.${reason}` })}
          </option>
        ))}
      </TextField>
      {value.reason === 'OTHER' && (
        <Box mt={2}>
          <TextField
            id={`${id}-other`}
            fullWidth
            multiline
            required
            minRows={3}
            value={value.other}
            onChange={(event) =>
              onChange({ ...value, other: event.target.value }, 'other')
            }
            label={intl.formatMessage({ id: 'ORDERS.REASON.OTHER_TEXT' })}
            error={Boolean(errors.other)}
            helperText={
              errors.other
                ? intl.formatMessage({ id: errors.other })
                : undefined
            }
            inputProps={{ maxLength: MAX_REASON_LENGTH + 1 }}
          />
        </Box>
      )}
    </>
  );
};

const hasReasonErrors = (errors: ReasonErrors) =>
  Boolean(errors.reason || errors.other);

interface CancelOrderDialogProps {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => unknown;
}

// Cancels a future order with a required internal reason. Used on the
// detail page and in the expanded row of All work orders.
export const CancelOrderDialog = ({
  open,
  busy,
  onClose,
  onSubmit,
}: CancelOrderDialogProps) => {
  const intl = useIntl();
  const [reason, setReason] = useState<ReasonState>(EMPTY_REASON);
  const [errors, setErrors] = useState<ReasonErrors>({});

  // Every opening starts with an empty reason.
  useEffect(() => {
    if (open) {
      setReason(EMPTY_REASON);
      setErrors({});
    }
  }, [open]);

  const submit = () => {
    const validation = validateOrderEndReason(reason.reason, reason.other);
    setErrors(validation);
    if (!hasReasonErrors(validation) && reason.reason) {
      onSubmit(toOrderEndReason(reason.reason, reason.other));
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {intl.formatMessage({ id: 'ORDERS.CANCEL_TITLE' })}
      </DialogTitle>
      <DialogContent>
        <Box mt={1}>
          <ReasonFields
            id="order-cancel-reason"
            autoFocus
            label={intl.formatMessage({ id: 'ORDERS.CANCELLATION_REASON' })}
            value={reason}
            errors={errors}
            onChange={(next, field) => {
              setReason(next);
              setErrors((current) => ({ ...current, [field]: undefined }));
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: 'ORDERS.BACK_ACTION' })}
        </Button>
        <DangerButton disabled={busy} onClick={submit}>
          {intl.formatMessage({ id: 'ORDERS.CANCEL_ORDER' })}
        </DangerButton>
      </DialogActions>
    </Dialog>
  );
};

interface EarlyEndOrderDialogProps {
  open: boolean;
  busy: boolean;
  // The order's planned end; the effective end may not be later.
  endDate: string;
  onClose: () => void;
  onSubmit: (request: OrderEarlyEndRequest) => unknown;
}

// Ends an active order early: an effective end date from today up to the
// planned end, and a required internal reason.
export const EarlyEndOrderDialog = ({
  open,
  busy,
  endDate,
  onClose,
  onSubmit,
}: EarlyEndOrderDialogProps) => {
  const intl = useIntl();
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [reason, setReason] = useState<ReasonState>(EMPTY_REASON);
  const [dateError, setDateError] = useState('');
  const [reasonErrors, setReasonErrors] = useState<ReasonErrors>({});

  // Every opening starts with an empty reason.
  useEffect(() => {
    if (open) {
      setReason(EMPTY_REASON);
      setDateError('');
      setReasonErrors({});
    }
  }, [open]);

  const submit = () => {
    const effective = moment(date, 'YYYY-MM-DD', true);
    if (
      !effective.isValid() ||
      effective.isBefore(moment().startOf('day')) ||
      effective.isAfter(moment(endDate).startOf('day'))
    ) {
      setDateError('ORDERS.VALIDATION.EFFECTIVE_DATE');
      return;
    }
    setDateError('');
    const validation = validateOrderEndReason(reason.reason, reason.other);
    setReasonErrors(validation);
    if (hasReasonErrors(validation) || !reason.reason) {
      return;
    }
    onSubmit({
      effectiveDate: effective.format('YYYY-MM-DD'),
      reason: toOrderEndReason(reason.reason, reason.other),
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {intl.formatMessage({ id: 'ORDERS.END_EARLY_TITLE' })}
      </DialogTitle>
      <DialogContent>
        <DateField
          id="order-end-date"
          autoFocus
          fullWidth
          value={date}
          onChange={(next) => {
            // Like the reason, the date error clears once the date changes.
            setDate(next);
            setDateError('');
          }}
          label={intl.formatMessage({ id: 'ORDERS.EFFECTIVE_END_DATE' })}
          min={moment().format('YYYY-MM-DD')}
          max={endDate}
          error={Boolean(dateError)}
          helperText={
            dateError ? intl.formatMessage({ id: dateError }) : undefined
          }
        />
        <Box mt={2}>
          <ReasonFields
            id="order-end-reason"
            label={intl.formatMessage({ id: 'ORDERS.END_REASON' })}
            value={reason}
            errors={reasonErrors}
            onChange={(next, field) => {
              setReason(next);
              setReasonErrors((current) => ({
                ...current,
                [field]: undefined,
              }));
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: 'ORDERS.BACK_ACTION' })}
        </Button>
        <Button color="secondary" onClick={submit} disabled={busy}>
          {intl.formatMessage({ id: 'ORDERS.END_EARLY' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
